import React, { useState, useEffect } from 'react';
import {
  Card, Row, Col, Spin, Alert, Empty, Button, Select, Input, Typography,
  Space, Avatar, Badge, Tooltip, Collapse, List, Divider, Image, Tag, Radio
} from 'antd';
import {
  FileImageOutlined, UserOutlined, SearchOutlined, FilterOutlined,
  ReloadOutlined, BarChartOutlined, ClockCircleOutlined, BulbOutlined,
  PictureOutlined, DownOutlined, CaretRightOutlined, DownloadOutlined
} from '@ant-design/icons';
import 'antd/dist/reset.css';
import Chart from 'react-apexcharts';
import { Link } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

const GeneratedImagesPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageData, setImageData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [groupedImages, setGroupedImages] = useState([]);

  // Metrics
  const [metrics, setMetrics] = useState({
    totalImages: 0,
    totalUsers: 0,
    avgImagesPerUser: 0,
    mostCommonPrompt: ''
  });

  // Chart states
  const [imagesByDay, setImagesByDay] = useState({
    categories: [],
    series: [{
      name: 'Images Generated',
      data: []
    }]
  });

  const [promptWordCloud, setPromptWordCloud] = useState({
    labels: [],
    series: []
  });

  // Add time scale state
  const [timeScale, setTimeScale] = useState('daily'); // 'daily', 'weekly', 'monthly'

  useEffect(() => {
    fetchImageData();
  }, []);

  useEffect(() => {
    if (imageData.length > 0) {
      calculateMetrics();
      prepareChartData();
      groupImagesByUser();
    }
  }, [imageData, searchText, timeScale]);

  const fetchImageData = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/getAllGeneratedImage', {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      if (data) {
        // Process image data
        const processedImageData = processImageData(data);
        setImageData(processedImageData);
      } else {
        throw new Error('Failed to fetch image data');
      }
    } catch (err) {
      console.error('Error fetching image data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const processImageData = (usersData) => {
    let allImages = [];
    
    usersData.forEach(userData => {
      const { user, images } = userData;
      
      if (images && images.length > 0) {
        images.forEach(image => {
          allImages.push({
            ...image,
            user_name: user.name || 'Unknown User',
            user_email: user.email || 'No email',
            user_uid: user.uid,
            user_avatar: user.dp_url,
            key: `${image.image_id}`
          });
        });
      }
    });
    
    return allImages;
  };

  const calculateMetrics = () => {
    if (imageData.length === 0) return;

    const totalImages = imageData.length;
    
    // Count unique users
    const uniqueUsers = new Set(imageData.map(img => img.user_uid));
    const totalUsers = uniqueUsers.size;
    
    const avgImagesPerUser = totalUsers > 0 ? (totalImages / totalUsers).toFixed(1) : 0;
    
    // Find most common prompt
    const promptCounts = {};
    imageData.forEach(img => {
      const prompt = img.prompt_text || 'Unknown';
      promptCounts[prompt] = (promptCounts[prompt] || 0) + 1;
    });
    
    let mostCommonPrompt = '';
    let maxCount = 0;
    Object.entries(promptCounts).forEach(([prompt, count]) => {
      if (count > maxCount) {
        mostCommonPrompt = prompt;
        maxCount = count;
      }
    });

    setMetrics({
      totalImages,
      totalUsers,
      avgImagesPerUser,
      mostCommonPrompt
    });
  };

  const prepareChartData = () => {
    if (imageData.length === 0) return;

    // Time scale calculations
    let timeScalePeriod, startDate;
    const now = new Date();
    
    switch(timeScale) {
      case 'daily':
        timeScalePeriod = 30; // Last 30 days
        startDate = new Date(now);
        startDate.setDate(now.getDate() - timeScalePeriod);
        break;
      case 'weekly':
        timeScalePeriod = 12; // Last 12 weeks
        startDate = new Date(now);
        startDate.setDate(now.getDate() - (timeScalePeriod * 7));
        break;
      case 'monthly':
      default:
        timeScalePeriod = 6; // Last 6 months
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - timeScalePeriod);
        break;
    }

    // Initialize time periods
    const timePeriods = {};
    
    if (timeScale === 'daily') {
      // Initialize last X days
      for (let i = 0; i < timeScalePeriod; i++) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + i);
        const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        timePeriods[dateStr] = 0;
      }
    } else if (timeScale === 'weekly') {
      // Initialize last X weeks
      for (let i = 0; i < timeScalePeriod; i++) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + (i * 7));
        const weekNumber = Math.ceil((d.getDate() + d.getDay()) / 7);
        const dateStr = `Week ${weekNumber}`;
        timePeriods[dateStr] = 0;
      }
    } else {
      // Monthly (default)
      for (let i = 0; i < timeScalePeriod; i++) {
        const d = new Date(startDate);
        d.setMonth(startDate.getMonth() + i);
        const monthYear = `${d.toLocaleString('default', { month: 'short' })} ${d.getFullYear()}`;
        timePeriods[monthYear] = 0;
      }
    }

    // Count images by time period
    imageData.forEach(img => {
      try {
        const imgDate = new Date(img.created_at);
        if (imgDate >= startDate) {
          let dateStr;
          
          if (timeScale === 'daily') {
            dateStr = imgDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          } else if (timeScale === 'weekly') {
            const weekNumber = Math.ceil((imgDate.getDate() + imgDate.getDay()) / 7);
            dateStr = `Week ${weekNumber}`;
          } else {
            dateStr = `${imgDate.toLocaleString('default', { month: 'short' })} ${imgDate.getFullYear()}`;
          }
          
          if (timePeriods[dateStr] !== undefined) {
            timePeriods[dateStr]++;
          }
        }
      } catch (err) {
        console.error('Error processing image date:', err);
      }
    });

    setImagesByDay({
      categories: Object.keys(timePeriods),
      series: [{
        name: 'Images Generated',
        data: Object.values(timePeriods)
      }]
    });

    // Word cloud data from prompts
    const wordCounts = {};
    imageData.forEach(img => {
      if (!img.prompt_text) return;
      
      const words = img.prompt_text.split(/\s+/);
      words.forEach(word => {
        if (word.length > 3) {
          const cleanWord = word.toLowerCase().replace(/[^\w\s]/gi, '');
          if (cleanWord) {
            wordCounts[cleanWord] = (wordCounts[cleanWord] || 0) + 1;
          }
        }
      });
    });
    
    // Get top 10 words
    const sortedWords = Object.entries(wordCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
    
    setPromptWordCloud({
      labels: sortedWords.map(([word]) => word),
      series: sortedWords.map(([_, count]) => count)
    });
  };

  const groupImagesByUser = () => {
    let result = [...imageData];
    
    // Apply search filter
    if (searchText) {
      const lowerSearchText = searchText.toLowerCase();
      result = result.filter(img => 
        (img.user_name && img.user_name.toLowerCase().includes(lowerSearchText)) ||
        (img.user_email && img.user_email.toLowerCase().includes(lowerSearchText)) ||
        (img.prompt_text && img.prompt_text.toLowerCase().includes(lowerSearchText))
      );
    }
    
    // Group by user
    const grouped = result.reduce((acc, image) => {
      const userId = image.user_uid;
      if (!acc[userId]) {
        acc[userId] = {
          user: {
            uid: userId,
            name: image.user_name,
            email: image.user_email,
            avatar: image.user_avatar
          },
          images: []
        };
      }
      acc[userId].images.push(image);
      return acc;
    }, {});
    
    // Sort images by date (newest first) within each user
    Object.values(grouped).forEach(group => {
      group.images.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    });
    
    // Convert to array and sort by most recent image
    const groupedArray = Object.values(grouped).sort((a, b) => {
      const latestA = a.images[0]?.created_at || 0;
      const latestB = b.images[0]?.created_at || 0;
      return new Date(latestB) - new Date(latestA);
    });
    
    setGroupedImages(groupedArray);
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const resetFilters = () => {
    setSearchText('');
  };

  // Chart options
  const lineOptions = {
    chart: {
      type: 'line',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      },
      zoom: {
        enabled: false
      }
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: 'smooth',
      width: 3
    },
    colors: ['var(--primary-color)'],
    xaxis: {
      categories: imagesByDay.categories
    },
    yaxis: {
      title: {
        text: 'Images'
      }
    },
    tooltip: {
      x: {
        format: 'dd MMM yyyy'
      }
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: "vertical",
        shadeIntensity: 0.25,
        gradientToColors: undefined,
        inverseColors: true,
        opacityFrom: 1,
        opacityTo: 0.7,
        stops: [0, 100]
      }
    }
  };

  const donutOptions = {
    chart: {
      type: 'donut',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      }
    },
    legend: {
      position: 'bottom'
    },
    dataLabels: {
      enabled: false
    },
    colors: ['var(--primary-color)', 'var(--secondary-color)', 'var(--accent-color)', 'var(--warning-color)'],
    plotOptions: {
      pie: {
        donut: {
          size: '60%',
          labels: {
            show: true,
            name: {
              show: true
            },
            value: {
              show: true,
              fontSize: '22px',
              fontWeight: 600
            },
            total: {
              show: true,
              label: 'Total',
              formatter: function (w) {
                return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
              }
            }
          }
        }
      }
    }
  };

  const renderImageItem = (image) => {
    const formattedDate = new Date(image.created_at).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    
    const formattedTime = new Date(image.created_at).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
    
    return (
      <List.Item 
        key={image.image_id}
        className="image-item"
        style={{
          padding: '16px',
          borderRadius: '8px',
          backgroundColor: 'var(--background-secondary)',
          marginBottom: '16px',
          transition: 'all 0.3s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          border: '1px solid var(--border-color)'
        }}
      >
        <div className="image-content" style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <div className="image-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div className="image-details" style={{ display: 'flex', flexDirection: 'column' }}>
              <div className="image-title" style={{ fontWeight: '600', fontSize: '16px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                {image.prompt_text || 'No prompt provided'}
              </div>
              <div className="image-subtitle" style={{ color: 'var(--text-tertiary)', fontSize: '14px' }}>
                Generated on {formattedDate} at {formattedTime}
              </div>
            </div>
            <div className="image-actions">
              <a href={image.image_url} download target="_blank" rel="noopener noreferrer">
                <Button 
                  icon={<DownloadOutlined />} 
                  type="default" 
                  size="small"
                  title="Download image"
                >
                  Download
                </Button>
              </a>
            </div>
          </div>
          
          <div className="image-preview" style={{ width: '100%', textAlign: 'center', marginBottom: '12px' }}>
            <Image
              src={image.image_url}
              alt={image.prompt_text || 'Generated image'}
              style={{ 
                maxHeight: '300px', 
                maxWidth: '100%',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
              placeholder={
                <div style={{ 
                  background: 'var(--background-tertiary)', 
                  height: '200px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <Spin />
                </div>
              }
            />
          </div>
          
          <div className="image-meta" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Tag color="blue" icon={<BulbOutlined />} style={{ borderRadius: '12px' }}>
              {image.image_id.substring(0, 8)}...
            </Tag>
          </div>
        </div>
      </List.Item>
    );
  };

  const renderUserPanel = (userGroup, index) => {
    const { user, images } = userGroup;
    const totalImages = images.length;
    
    return (
      <Panel 
        key={user.uid || index}
        header={
          <div className="user-panel-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <div className="user-info" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Avatar 
                src={user.avatar} 
                icon={!user.avatar && <UserOutlined />} 
                size="large"
                style={{ backgroundColor: 'var(--primary-color)' }}
              />
              <div className="user-details">
                <div className="user-name" style={{ fontWeight: '600', fontSize: '16px' }}>{user.name || 'Unknown User'}</div>
                <div className="user-email" style={{ color: 'var(--text-tertiary)', fontSize: '14px' }}>{user.email || 'No email'}</div>
              </div>
            </div>
            <div className="user-stats" style={{ display: 'flex', gap: '24px' }}>
              <div className="stat-item">
                <span className="stat-label" style={{ marginRight: '8px', color: 'var(--text-tertiary)' }}>Images:</span>
                <span className="stat-value" style={{ fontWeight: '600' }}>{totalImages}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label" style={{ marginRight: '8px', color: 'var(--text-tertiary)' }}>Latest:</span>
                <span className="stat-value" style={{ fontWeight: '600' }}>
                  {new Date(images[0]?.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        }
      >
        <List
          className="images-list"
          dataSource={images}
          renderItem={renderImageItem}
          grid={{ 
            gutter: 16, 
            xs: 1, 
            sm: 1, 
            md: 2, 
            lg: 2, 
            xl: 3,
            xxl: 3
          }}
          pagination={{
            pageSize: 6,
            size: 'small',
            showSizeChanger: false
          }}
        />
        <div className="user-actions" style={{ marginTop: '16px', textAlign: 'right' }}>
          <Link to={`/users?uid=${user.uid}`}>
            <Button type="primary" ghost>
              View User Profile
            </Button>
          </Link>
        </div>
      </Panel>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '2rem' }}>
        <Spin size="large" tip="Loading image data..." spinning={true} />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Data"
        description={error}
        type="error"
        showIcon
        style={{ margin: '2rem' }}
      />
    );
  }

  return (
    <div style={{ padding: '24px', background: 'var(--background-primary)', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={3} style={{ color: 'var(--primary-color)', margin: 0 }}>
          <FileImageOutlined /> Generated Images
        </Title>
        <Text type="secondary">
          View and manage AI-generated images created by users
        </Text>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card primary">
            <div>
              <div className="stat-value">{metrics.totalImages}</div>
              <div className="stat-label">Total Images</div>
            </div>
            <FileImageOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card secondary">
            <div>
              <div className="stat-value">{metrics.totalUsers}</div>
              <div className="stat-label">Unique Users</div>
            </div>
            <UserOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card accent">
            <div>
              <div className="stat-value">{metrics.avgImagesPerUser}</div>
              <div className="stat-label">Avg. Images Per User</div>
            </div>
            <BarChartOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card warning">
            <div>
              <div className="stat-value">{metrics.mostCommonPrompt ? metrics.mostCommonPrompt.substring(0, 15) + (metrics.mostCommonPrompt.length > 15 ? '...' : '') : 'None'}</div>
              <div className="stat-label">Most Common Prompt</div>
            </div>
            <BulbOutlined className="stat-icon" />
          </div>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={12}>
          <Card 
            title="Image Generation Trend" 
            className="dashboard-card"
            bodyStyle={{ height: '300px' }}
            extra={
              <Radio.Group 
                value={timeScale}
                onChange={e => setTimeScale(e.target.value)}
                size="small"
                buttonStyle="solid"
              >
                <Radio.Button value="daily">Daily</Radio.Button>
                <Radio.Button value="weekly">Weekly</Radio.Button>
                <Radio.Button value="monthly">Monthly</Radio.Button>
              </Radio.Group>
            }
          >
            <div style={{ height: '280px', width: '100%' }}>
              {imagesByDay.series[0].data.length > 0 ? (
                <Chart
                  options={{
                    ...lineOptions,
                    xaxis: {
                      ...lineOptions.xaxis,
                      categories: imagesByDay.categories,
                      labels: {
                        rotate: -45,
                        style: { fontSize: '12px' }
                      }
                    },
                    grid: {
                      padding: {
                        left: 10,
                        right: 10
                      }
                    }
                  }}
                  series={imagesByDay.series}
                  type="line"
                  height="280"
                  width="100%"
                />
              ) : (
                <Empty description="No image data available" style={{ marginTop: '80px' }} />
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            title="Common Prompt Keywords" 
            className="dashboard-card"
            bodyStyle={{ height: '300px' }}
          >
            <div style={{ height: '280px', width: '100%' }}>
              {promptWordCloud.series.some(value => value > 0) ? (
                <Chart
                  options={{
                    ...donutOptions,
                    labels: promptWordCloud.labels,
                    chart: {
                      ...donutOptions.chart,
                      width: '100%'
                    }
                  }}
                  series={promptWordCloud.series}
                  type="donut"
                  height="280"
                  width="100%"
                />
              ) : (
                <Empty description="No prompt data available" style={{ marginTop: '80px' }} />
              )}
            </div>
          </Card>
        </Col>
      </Row>

      <Card className="dashboard-card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', marginBottom: '16px', gap: '16px', flexWrap: 'wrap' }}>
          <Input
            placeholder="Search by name, email or prompt text"
            value={searchText}
            onChange={handleSearch}
            style={{ maxWidth: '300px' }}
            prefix={<SearchOutlined />}
            allowClear
          />
          
          <div style={{ marginLeft: 'auto' }}>
            <Button 
              type="primary" 
              icon={<ReloadOutlined />}
              onClick={fetchImageData}
            >
              Refresh
            </Button>
          </div>
        </div>

        {groupedImages.length > 0 ? (
          <Collapse 
            className="user-images-collapse" 
            expandIcon={({ isActive }) => isActive ? <CaretRightOutlined rotate={90} /> : <CaretRightOutlined />}
          >
            {groupedImages.map((group, index) => renderUserPanel(group, index))}
          </Collapse>
        ) : (
          <Empty description="No images found matching your filters" />
        )}
      </Card>
    </div>
  );
};

export default GeneratedImagesPage;